// Per-domain barrels and the two top-level discriminated unions.
//
// (protocol.md §3 / protocol-package.md §2.2)
import { z } from 'zod';
import { adminKickAckSchema, adminKickRejectedSchema, adminKickSchema } from './admin.js';
import { analyticsInviteSchema, analyticsLayoutSwitchSchema, analyticsTimelapseShareSchema, analyticsTipSchema, analyticsTouchGestureSchema, } from './analytics.js';
import { chatMessageSchema, chatSendSchema } from './chat.js';
import { feedbackAckSchema, feedbackRejectedSchema, feedbackSubmitSchema } from './feedback.js';
import { memberCursorChangedSchema, memberCursorChangeSchema, memberCursorDraftedSchema, memberCursorDraftSchema, memberNameChangedSchema, memberNameChangeSchema, memberRomChangedSchema, memberRomSchema, } from './member.js';
import { roomJoinSchema, roomMemberJoinedSchema, roomMemberLeftSchema, roomRejectedSchema, roomResumeOkSchema, roomResumeSchema, roomWelcomeSchema, } from './room.js';
import { snapshotPublishedSchema } from './snapshot.js';
import { strokeCancelledBroadcastSchema, strokeCancelRejectedSchema, strokeCancelSchema, strokeEndBroadcastSchema, strokeEndSchema, strokeSegmentBroadcastSchema, strokeSegmentSchema, strokeStartBroadcastSchema, strokeStartSchema, } from './stroke.js';
import { whiteboardStrokeLogTailRequestSchema, whiteboardStrokeLogTailSchema, } from './strokeLogTail.js';
import { systemAnnounceClearedSchema, systemAnnouncePostedSchema, systemErrorSchema, systemMaintenanceCancelledSchema, systemMaintenanceCountdownSchema, systemMaintenanceScheduledSchema, } from './system.js';
import { whiteboardBackgroundSetBroadcastSchema, whiteboardBackgroundSetSchema, whiteboardClearBroadcastSchema, whiteboardClearSchema, whiteboardImagepasteConfirmSchema, whiteboardImagepasteRejectedSchema, whiteboardImagepasteRequestSchema, whiteboardImagepasteSchema, whiteboardImagepasteTokenSchema, whiteboardRegionCancelledSchema, whiteboardRegionCancelRejectedSchema, whiteboardRegionCancelSchema, whiteboardRegionConfirmSchema, whiteboardRegionRejectedSchema, whiteboardRegionRequestSchema, whiteboardRegionSchema, whiteboardRegionTokenSchema, whiteboardRestoreRejectedSchema, whiteboardRestoreRequestSchema, whiteboardRestoreSchema, } from './whiteboard.js';
export * from './admin.js';
export * from './analytics.js';
export * from './chat.js';
export * from './feedback.js';
export * from './member.js';
export * from './room.js';
export * from './snapshot.js';
export * from './stroke.js';
export * from './strokeLogTail.js';
export * from './system.js';
export * from './whiteboard.js';
/**
 * `ClientEvent` — every payload the server may receive on the WS path.
 *
 * Stroke / whiteboard.clear events appear here in their pre-broadcast
 * shape (no server-stamped `uid` / `seq` / `ts`).
 */
export const clientEventSchema = z.discriminatedUnion('t', [
    // stroke.* (uniform, but client-side variant has no server stamps)
    strokeStartSchema,
    strokeSegmentSchema,
    strokeEndSchema,
    strokeCancelSchema,
    // room.*
    roomJoinSchema,
    roomResumeSchema,
    // chat.*
    chatSendSchema,
    // whiteboard.*
    whiteboardClearSchema,
    whiteboardBackgroundSetSchema,
    whiteboardImagepasteRequestSchema,
    whiteboardImagepasteConfirmSchema,
    whiteboardRegionRequestSchema,
    whiteboardRegionConfirmSchema,
    whiteboardRegionCancelSchema,
    whiteboardRestoreRequestSchema,
    // whiteboard.stroke-log-tail (絵ログ live tail request)
    whiteboardStrokeLogTailRequestSchema,
    // member.*
    memberRomSchema,
    memberNameChangeSchema,
    memberCursorChangeSchema,
    memberCursorDraftSchema,
    // admin.*
    adminKickSchema,
    // feedback.*
    feedbackSubmitSchema,
    // analytics.* (client-initiated instrumentation)
    analyticsTimelapseShareSchema,
    analyticsLayoutSwitchSchema,
    analyticsInviteSchema,
    analyticsTipSchema,
    analyticsTouchGestureSchema,
]);
/**
 * `ServerEvent` — every payload the client may receive on the WS path.
 *
 * Stroke / whiteboard.clear broadcasts appear here in the server-stamped
 * shape. `room.resume-ok` is itself an inner `discriminatedUnion('mode')`
 * over `delta` and `snapshot` cargo (see `room.ts`).
 */
export const serverEventSchema = z.discriminatedUnion('t', [
    // stroke.* (broadcast variants — server-stamped uid/seq/ts)
    strokeStartBroadcastSchema,
    strokeSegmentBroadcastSchema,
    strokeEndBroadcastSchema,
    strokeCancelledBroadcastSchema,
    strokeCancelRejectedSchema,
    // snapshot.* (transient broadcast — no stamps, not buffered)
    snapshotPublishedSchema,
    // room.*
    roomWelcomeSchema,
    roomRejectedSchema,
    roomMemberJoinedSchema,
    roomMemberLeftSchema,
    roomResumeOkSchema,
    // chat.*
    chatMessageSchema,
    // whiteboard.*
    whiteboardClearBroadcastSchema,
    whiteboardBackgroundSetBroadcastSchema,
    whiteboardImagepasteTokenSchema,
    whiteboardImagepasteRejectedSchema,
    whiteboardImagepasteSchema,
    whiteboardRegionTokenSchema,
    whiteboardRegionRejectedSchema,
    whiteboardRegionSchema,
    whiteboardRegionCancelledSchema,
    whiteboardRegionCancelRejectedSchema,
    whiteboardRestoreRejectedSchema,
    whiteboardRestoreSchema,
    // whiteboard.stroke-log-tail (絵ログ live tail response)
    whiteboardStrokeLogTailSchema,
    // member.*
    memberRomChangedSchema,
    memberNameChangedSchema,
    memberCursorChangedSchema,
    // member.cursor-drafted (transient broadcast — no stamps, not buffered)
    memberCursorDraftedSchema,
    // admin.*
    adminKickAckSchema,
    adminKickRejectedSchema,
    // feedback.*
    feedbackAckSchema,
    feedbackRejectedSchema,
    // system.*
    systemMaintenanceScheduledSchema,
    systemMaintenanceCountdownSchema,
    systemMaintenanceCancelledSchema,
    systemAnnouncePostedSchema,
    systemAnnounceClearedSchema,
    systemErrorSchema,
]);
//# sourceMappingURL=index.js.map